import {
  useCallback,
  useDeferredValue,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { getNews } from "../../../services/newsServices";

import {
  ensureArray,
  isActiveNews,
  normalizeNewsItem,
  normalizeSearchText,
  sortNews,
} from "../utils/newsUtils";

const INITIAL_FILTERS = {
  q: "",
  year: "all",
  sort: "created_at",
  dir: "DESC",
  limit: 9,
};

export function useNewsPage() {
  const bootedRef = useRef(false);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [allItems, setAllItems] = useState([]);
  const [filters, setFilters] = useState(INITIAL_FILTERS);
  const [page, setPage] = useState(1);
  const [selectedNews, setSelectedNews] = useState(null);

  const deferredSearch = useDeferredValue(filters.q);

  const loadNews = useCallback(async () => {
    setLoading(true);
    setError("");

    try {
      const response = await getNews();
      const rows = ensureArray(response)
        .filter(isActiveNews)
        .map(normalizeNewsItem);

      setAllItems(rows);
    } catch (err) {
      console.error("Erreur chargement actualités:", err);
      setAllItems([]);
      setError("LOAD_ERROR");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (bootedRef.current) return;
    bootedRef.current = true;
    loadNews();
  }, [loadNews]);

  const years = useMemo(() => {
    return Array.from(
      new Set(
        allItems
          .map((item) => item.year)
          .filter(Boolean)
      )
    ).sort((a, b) => Number(b) - Number(a));
  }, [allItems]);

  const filteredItems = useMemo(() => {
    const q = normalizeSearchText(deferredSearch);

    const filtered = allItems.filter((item) => {
      const matchYear = filters.year === "all" ? true : item.year === filters.year;
      const matchSearch = q ? item.searchIndex.includes(q) : true;

      return matchYear && matchSearch;
    });

    return sortNews(filtered, filters.sort, filters.dir);
  }, [allItems, deferredSearch, filters.year, filters.sort, filters.dir]);

  const total = filteredItems.length;

  const totalPages = useMemo(() => {
    return Math.max(Math.ceil(total / filters.limit), 1);
  }, [filters.limit, total]);

  const paginatedItems = useMemo(() => {
    const safePage = Math.min(page, totalPages);
    const start = (safePage - 1) * filters.limit;
    return filteredItems.slice(start, start + filters.limit);
  }, [filteredItems, filters.limit, page, totalPages]);

  useEffect(() => {
    if (page > totalPages) {
      setPage(totalPages);
    }
  }, [page, totalPages]);

  const updateFilter = useCallback((name, value) => {
    setPage(1);
    setSelectedNews(null);

    setFilters((previous) => ({
      ...previous,
      [name]: value,
    }));
  }, []);

  const resetFilters = useCallback(() => {
    setFilters(INITIAL_FILTERS);
    setPage(1);
    setSelectedNews(null);
  }, []);

  const toggleYear = useCallback((year) => {
    setPage(1);
    setSelectedNews(null);

    setFilters((previous) => ({
      ...previous,
      year: previous.year === year ? "all" : year,
    }));
  }, []);

  const setLimit = useCallback((limit) => {
    setPage(1);

    setFilters((previous) => ({
      ...previous,
      limit: Number(limit) || 9,
    }));
  }, []);

  const setSort = useCallback((sort) => {
    setPage(1);

    setFilters((previous) => ({
      ...previous,
      sort,
    }));
  }, []);

  const setDir = useCallback((dir) => {
    setPage(1);

    setFilters((previous) => ({
      ...previous,
      dir,
    }));
  }, []);

  const goToPrevPage = useCallback(() => {
    setPage((current) => Math.max(current - 1, 1));
  }, []);

  const goToNextPage = useCallback(() => {
    setPage((current) => Math.min(current + 1, totalPages));
  }, [totalPages]);

  const openNews = useCallback((item) => {
    setSelectedNews(item);
  }, []);

  const closeNews = useCallback(() => {
    setSelectedNews(null);
  }, []);

  const refresh = useCallback(() => {
    setSelectedNews(null);
    loadNews();
  }, [loadNews]);

  return {
    loading,
    error,

    items: filteredItems,
    paginatedItems,
    total,
    allTotal: allItems.length,
    totalPages,

    years,
    filters,
    page,

    selectedNews,

    updateFilter,
    resetFilters,
    toggleYear,
    setLimit,
    setSort,
    setDir,
    goToPrevPage,
    goToNextPage,
    refresh,
    openNews,
    closeNews,
  };
}